import { useEffect, useState } from 'react'

import { FormattedMessage, useIntl } from 'react-intl'

import { DangerModal } from '@/lib/core/components/Modal'
import { ResponseError } from '@/lib/core/interfaces/common.types'

import messages from './DeleteTaskModal.messages'
import { useDeleteTaskAssignment } from '../../api/useDeleteTask'
import { TaskDefinition } from '../../types/task'

type DeleteTaskModalProps = {
  closeModal: (confirmed?: boolean) => void
  isOpen: boolean
  task: TaskDefinition
  onTaskDeleted?: () => void
}

export const DeleteTaskModal = ({
  closeModal,
  isOpen = false,
  task,
  onTaskDeleted,
}: DeleteTaskModalProps) => {
  const { formatMessage } = useIntl()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const { trigger: deleteTaskAssignment } = useDeleteTaskAssignment(task.taskDefinition.id, task.id)

  useEffect(() => {
    if (isOpen) {
      setIsLoading(false)
      setError('')
    }
  }, [isOpen])

  const deleteProcess = async () => {
    try {
      await deleteTaskAssignment()
      onTaskDeleted?.()
      closeModal(true)
    } catch (err) {
      const error = (err as ResponseError).response as Response
      const errorData = await error?.json()
      if (errorData?.status) {
        setError(errorData.status)
      } else {
        setError(formatMessage(messages.unknownError))
      }
    }
  }

  const onConfirm = async () => {
    setIsLoading(true)
    setError('')
    await deleteProcess()
    setIsLoading(false)
  }

  return (
    <DangerModal
      data-testid={`delete-task-modal-${task.id}`}
      isActive={isOpen}
      title={formatMessage(messages.title)}
      cancelProps={{ disabled: isLoading }}
      cancelLabel={formatMessage(messages.cancelButton)}
      confirmLabel={
        error ? formatMessage(messages.tryAgain) : formatMessage(messages.confirmButton)
      }
      confirmProps={{ isLoading }}
      onCancel={() => closeModal(false)}
      onConfirm={onConfirm}
    >
      {error || (
        <FormattedMessage
          {...messages.modalMessage}
          values={{ taskReference: task.taskDefinition.reference }}
        />
      )}
    </DangerModal>
  )
}
